/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { ListenerMiddlewareInstance } from "@reduxjs/toolkit";

import { EasFlowEvent, EasLogger, Log } from "../../model/logging";
import { DiagnosticSeverity } from "../../model/types";
import { hideEasOsd, showOsd } from "../osd";

const TAG: string = "easRouteRestore";

/**
 * A location an Emergency Alert System (EAS) alert interrupted.
 *
 * The whole location, not just the pathname: several screens seed themselves
 * from `location.state` — Content Detail, Manage Series Recordings, Scheduled
 * Series — and restoring only the path lands the viewer back on a screen with
 * nothing to render. `search` and `hash` carry the same risk for any route
 * that grows a query parameter later.
 *
 * Members are in alphabetical order (CodingGuidelines §8.2).
 */
export interface TEasRoute {
  /** Fragment identifier, including the leading `#`, or `""`. */
  hash: string;

  /** Path portion of the location. */
  pathname: string;

  /** Query string, including the leading `?`, or `""`. */
  search: string;

  /** Opaque per-route seed data handed over via `useLocation().state`. */
  state: unknown;
}

/**
 * How this module reads and changes the route.
 *
 * A port rather than a direct dependency on the router: `src/viewModel/` must
 * not import `src/views/` (the `viewmodel-to-views` layering rule is zero
 * tolerance), and the router instance is created there. The composition root
 * in `src/index.tsx` — which sits outside all three layers and already holds
 * the router — supplies the implementation. Same arrangement as
 * `EamModelImpl.setPlaybackProvider()`.
 *
 * Members are in alphabetical order (CodingGuidelines §8.2).
 */
export interface IEasRouteNavigator {
  /**
   * The location the viewer is on right now.
   *
   * @returns The current route
   */
  getCurrentRoute(): TEasRoute;

  /**
   * Send the viewer back to a location, replacing the current history entry.
   *
   * The implementation owns everything an ordinary navigation does — the
   * `notifyBeforeNavigate` hand-off to the PiP coordinator included — so this
   * module needs no import of the player thunks, which would pull the whole
   * Guide slice into the store's own module graph.
   *
   * @param route - The route to restore
   */
  restoreRoute(route: TEasRoute): void;
}

/** The registered navigator, or `null` before the composition root wires one. */
let mNavigator: IEasRouteNavigator | null = null;

/** The route the alert interrupted, or `null` when no alert is up. */
let mInterruptedRoute: TEasRoute | null = null;

/**
 * Register how this module reaches the router.
 *
 * Called once from the composition root. Without it an alert still displays
 * and still ends; only the route restore is skipped, which is logged rather
 * than passed over silently.
 *
 * @param navigator - The navigator to use, or `null` to deregister
 */
export function setEasRouteNavigator(
  navigator: IEasRouteNavigator | null
): void {
  mNavigator = navigator;
  Log.i(
    TAG,
    `setEasRouteNavigator(): ${navigator !== null ? "registered" : "cleared"}`
  );
}

/**
 * Drop any recorded route. Test-only.
 */
export function __resetEasRouteRestoreForTest(): void {
  mNavigator = null;
  mInterruptedRoute = null;
}

/**
 * registerEasRouteRestoreListeners — keeps an EAS alert from changing which
 * screen the viewer is on.
 *
 * An EAS alert is an overlay, not a destination: whichever screen it
 * interrupts is the screen the viewer must be returned to when it ends,
 * whether it ended because they dismissed it or because it ran out. That is
 * true of every screen equally — Home, Guide, Search, full-screen playback —
 * so the rule is enforced here, once, rather than per screen.
 *
 * ## Why this exists rather than relying on input blocking alone
 *
 * `EasInputGate` already swallows input while an alert is up, which should
 * mean no screen ever sees the key that would navigate. This is the backstop
 * for everything that can move the route *without* going through the input
 * pipeline: an effect on some other screen, a player state change, a
 * redirect. Those all end with the viewer somewhere they did not choose to
 * be — most visibly on the player, because `HomeScreenController` maps BACK
 * to "go to the player".
 *
 * ## Why at the store rather than in a component
 *
 * This was a mounted component that tracked the route in refs and navigated
 * from a `useEffect`. REACT_ROUTER_GOVERNANCE forbids all three — §7.3
 * component-level history tracking, §10.1 navigation in an effect without
 * cleanup, §10.4 component state for route tracking — and ARCHITECTURE §5.5
 * reserves `views/common` for UI, not app flow. Driving it from the two
 * actions that bracket an alert's life removes the component, the refs and
 * the effect together, and makes the capture happen in the same tick the
 * alert takes the screen rather than a render later.
 *
 * @param listenerMiddleware - The store's listener middleware instance
 */
export function registerEasRouteRestoreListeners(
  listenerMiddleware: ListenerMiddlewareInstance
): void {
  // Alert on screen — remember where the viewer was.
  //
  // Keyed on the action rather than on slice state, so the route is read
  // before any of the alert's own effects can move it. An alert replacing one
  // already on screen must not re-record: the route to go back to is the one
  // the *first* alert interrupted, exactly as with the playback snapshot.
  listenerMiddleware.startListening({
    actionCreator: showOsd,
    effect: (action): void => {
      // `||`, not `??`: "" is the field's own falsy value and its documented
      // default, so the two behave identically here and the repo bans `??`
      // for TV legacy safety.
      const easCountry: string = action.payload.easCountry || "";
      if (easCountry.length === 0) {
        return;
      }
      if (mInterruptedRoute !== null) {
        Log.d(
          TAG,
          "showOsd(): route already recorded — keeping the first alert's"
        );
        return;
      }
      if (mNavigator === null) {
        Log.w(
          TAG,
          "showOsd(): no route navigator registered — the screen the alert " +
            "interrupted cannot be restored"
        );
        return;
      }
      mInterruptedRoute = mNavigator.getCurrentRoute();
      Log.i(
        TAG,
        `showOsd(): EAS alert shown on ${mInterruptedRoute.pathname} — ` +
          "route recorded"
      );
      EasLogger.logFlowEvent(
        EasFlowEvent.RouteRecorded,
        "EAS recorded the screen it interrupted",
        DiagnosticSeverity.Info,
        undefined,
        {
          // The path is a screen name, not viewer data. `search`, `hash` and
          // `state` are not logged: they carry content ids and seed data.
          pathname: mInterruptedRoute.pathname,
          has_state: String(mInterruptedRoute.state !== null)
        }
      );
    }
  });

  // Alert ended — put the viewer back if anything moved them.
  listenerMiddleware.startListening({
    actionCreator: hideEasOsd,
    effect: (): void => {
      const interrupted: TEasRoute | null = mInterruptedRoute;
      mInterruptedRoute = null;

      if (interrupted === null || mNavigator === null) {
        return;
      }

      const current: TEasRoute = mNavigator.getCurrentRoute();
      if (
        current.pathname === interrupted.pathname &&
        current.search === interrupted.search &&
        current.hash === interrupted.hash
      ) {
        Log.i(TAG, `hideEasOsd(): ended on ${current.pathname} — unchanged`);
        return;
      }

      Log.i(
        TAG,
        `hideEasOsd(): ended on ${current.pathname} — ` +
          `restoring ${interrupted.pathname}`
      );
      EasLogger.logFlowEvent(
        EasFlowEvent.RouteRestored,
        "EAS returned the viewer to the screen it interrupted",
        DiagnosticSeverity.Info,
        undefined,
        { from_pathname: current.pathname, to_pathname: interrupted.pathname }
      );
      mNavigator.restoreRoute(interrupted);
    }
  });
}
